import React from 'react';
import { Helmet } from "react-helmet-async";

/**
 * Meta Component - SEO meta tags for all pages
 *
 * Basic usage (non-product pages):
 *   <Meta title="About Us" description="Learn about RetroDayz" />
 *
 * Product page usage:
 *   <Meta
 *     title={product.name}
 *     description={product.description}
 *     keywords={`${product.brand}, ${product.character}, ${product.productType}`}
 *     image={product.image}
 *     url={`https://retrodayz.co.uk/product/${product.slug}`}
 *     product={product}
 *   />
 */
const Meta = ({ title, description, keywords, image, url, product }) => {
    const siteName = 'RetroDayz';
    const baseUrl = 'https://retrodayz.co.uk';

    // Build the full page title
    const pageTitle = title === 'Welcome To RetroDayz'
        ? title
        : `${title} | ${siteName}`;

    // Truncate description for meta (Google shows ~155 chars)
    const metaDescription = description && description.length > 155
        ? description.substring(0, 152) + '...'
        : description;

    // Build full image URL
    const metaImage = image
        ? (image.startsWith('http') ? image : `${baseUrl}${image}`)
        : `${baseUrl}/logo192.png`;

    // Build canonical URL
    const canonicalUrl = url || baseUrl;

    // Build JSON-LD structured data for product pages
    const productSchema = product ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.imageArray && product.imageArray.length > 0
            ? product.imageArray.map(img => img.startsWith('http') ? img : `${baseUrl}${img}`)
            : [metaImage],
        brand: {
            '@type': 'Brand',
            name: product.brand || siteName,
        },
        sku: product.sku || product._id,
        url: canonicalUrl,
        category: product.productType || product.category,
        offers: {
            '@type': 'Offer',
            price: product.sale && product.salePrice ? product.salePrice : product.price,
            priceCurrency: 'GBP',
            availability: product.countInStock > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
            url: canonicalUrl,
            seller: {
                '@type': 'Organization',
                name: siteName,
            },
            shippingDetails: {
                '@type': 'OfferShippingDetails',
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: 'GB',
                },
                deliveryTime: {
                    '@type': 'ShippingDeliveryTime',
                    handlingTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 1,
                        maxValue: 2,
                        unitCode: 'DAY',
                    },
                    transitTime: {
                        '@type': 'QuantitativeValue',
                        minValue: 2,
                        maxValue: 5,
                        unitCode: 'DAY',
                    },
                },
            },
            hasMerchantReturnPolicy: {
                '@type': 'MerchantReturnPolicy',
                applicableCountry: 'GB',
                returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                merchantReturnDays: 14,
                returnMethod: 'https://schema.org/ReturnByMail',
            },
        },
        ...(product.reviews && product.reviews.length > 0 ? {
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: product.rating.toFixed(1),
                reviewCount: product.numReviews,
                bestRating: '5',
                worstRating: '1',
            },
        } : {}),
    } : null;

    return (
        <Helmet>
            {/* Core Meta */}
            <title>{pageTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={keywords} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph (Facebook, LinkedIn, etc.) */}
            <meta property="og:type" content={product ? 'product' : 'website'} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={metaImage} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content={siteName} />

            {/* Product-specific Open Graph */}
            {product && (
                <>
                    <meta property="product:price:amount" content={product.sale && product.salePrice ? product.salePrice : product.price} />
                    <meta property="product:price:currency" content="GBP" />
                    <meta property="product:availability" content={product.countInStock > 0 ? 'in stock' : 'out of stock'} />
                </>
            )}

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={metaImage} />

            {/* JSON-LD Structured Data for Products */}
            {productSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(productSchema)}
                </script>
            )}
        </Helmet>
    );
};

Meta.defaultProps = {
    title: 'Welcome To RetroDayz',
    description: 'RetroDayz brings the classics back to life. Shop gaming T-shirts, lamps, mugs, collectibles, and pop culture gifts from the icons of a generation.',
    keywords: 'retro gaming, pop culture, Marvel, DC, gaming merchandise, retro t-shirts, gaming mugs, retro lamps, collectibles, gifts for gamers, RetroDayz',
};

export default Meta;